import { useState } from "react";

const input = "w-full rounded-md border border-border px-3 py-2 text-sm outline-none transition-[color,box-shadow] focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30 text-foreground placeholder:text-muted-foreground";
const label = "mb-1 block text-xs font-medium text-muted-foreground";

const LEVEL_RANK = { national: 0, county: 1, constituency: 2 };

/**
 * The Kenya 2027 module's own step, shown inside PlacementPicker once that cluster is
 * ticked and a race tag is chosen. Ported from the woza-tickets prototype's
 * Ke2027Detail.jsx: the administrative unit a race needs (deepest of the chosen races —
 * a constituency choice satisfies a county-level race too, since a constituency already
 * names its county) and the formations contesting it, with inline "+ Add a party" since
 * Kenyan parties are founded, renamed and dissolved between elections.
 */
export default function Ke2027Detail({
  races,
  tagIds = [],
  county,
  constituency,
  partyIds = [],
  counties = [],
  constituencies = [],
  parties = [],
  blocs = [],
  onChange,
  onCreateParty,
  errors = {},
}) {
  const [newParty, setNewParty] = useState({ name: "", abbr: "", blocId: "" });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const chosenRaces = (races || []).filter((r) => tagIds.includes(r.id));

  let need = { level: "national", optional: true };
  for (const r of chosenRaces) {
    if (!r.level) continue;
    if (LEVEL_RANK[r.level] > LEVEL_RANK[need.level]) need.level = r.level;
    if (!r.unitOptional && r.level !== "national") need.optional = false;
  }
  if (need.level === "national") need.optional = true;

  const countyConstituencies = constituencies.filter((c) => c.countyName === county);
  const wardApprox = chosenRaces.some((r) => r.wardApprox);
  const offHub = chosenRaces.filter((r) => r.onHub === false);

  if (!chosenRaces.length) {
    return <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Pick a race above and this will ask which county or constituency it is contested in.</p>;
  }

  async function addParty(e) {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      const res = await onCreateParty({ name: newParty.name.trim(), abbr: newParty.abbr.trim() || undefined, blocId: newParty.blocId || null });
      onChange({ partyIds: [...new Set([...partyIds, res.party.id])] });
      setNewParty({ name: "", abbr: "", blocId: "" });
      setShowAdd(false);
    } catch (err) {
      setAddError(err.message || "Could not add that party.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="mt-4 space-y-4 rounded-xl bg-gray-50 p-3 dark:bg-navy-900/40">
      {need.level !== "national" && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Where is it contested?</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {need.level === "constituency" ? "This race runs in one of the 290 constituencies." : "This race runs in one of the 47 counties."}
            {need.optional && " Leave it blank for a national market."}
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={label}>County{!need.optional && <span className="text-destructive"> *</span>}</label>
              <select className={input} value={county || ""} onChange={(e) => onChange({ county: e.target.value || null, constituency: null })}>
                <option value="">{need.optional ? "Nationwide" : "Choose a county"}</option>
                {counties.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
              {errors.county && <p className="mt-1 text-xs font-medium text-red-500">{errors.county}</p>}
            </div>
            {need.level === "constituency" && (
              <div>
                <label className={label}>Constituency{!need.optional && <span className="text-destructive"> *</span>}</label>
                <select className={input} value={constituency || ""} disabled={!county} onChange={(e) => onChange({ constituency: e.target.value || null })}>
                  <option value="">{county ? "Choose a constituency" : "Choose a county first"}</option>
                  {countyConstituencies.map((c) => (
                    <option key={c.no} value={c.name}>{c.no}. {c.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">{county ? `${countyConstituencies.length} in ${county}` : "Choose a county first"}</p>
                {errors.constituency && <p className="mt-1 text-xs font-medium text-red-500">{errors.constituency}</p>}
              </div>
            )}
          </div>
          {wardApprox && (
            <p className="mt-2 text-xs font-medium text-amber-600">
              The MCA race is contested at ward level — this build has no ward boundaries, so the market is pinned to its constituency; say which ward in the question itself.
            </p>
          )}
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-muted-foreground">Parties &amp; affiliation</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">These races resolve to a formation rather than to Yes/No. Pick the parties in play.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {parties.map((p) => {
            const on = partyIds.includes(p.id);
            const bloc = blocs.find((b) => b.id === p.blocId);
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={on}
                title={bloc ? `${p.name} · ${bloc.name}` : p.name}
                onClick={() => onChange({ partyIds: on ? partyIds.filter((x) => x !== p.id) : [...partyIds, p.id] })}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  on ? "bg-navy-700 text-white dark:bg-white dark:text-navy-800" : "border border-gray-200 bg-white text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:bg-transparent dark:text-white dark:hover:bg-white/5"
                }`}
              >
                {bloc && <span className="h-2 w-2 flex-none rounded-full" style={{ background: bloc.color || "#999" }} />}
                {p.abbr}
                {p.isCustom && <span className="text-[10px] opacity-60">new</span>}
              </button>
            );
          })}
          <button type="button" onClick={() => setShowAdd((v) => !v)} className="rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-white/20 dark:text-gray-300 dark:hover:bg-white/5">
            + Add a party
          </button>
        </div>

        {showAdd && (
          <form onSubmit={addParty} className="mt-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className={label}>Party name</label>
                <input className={input} required value={newParty.name} placeholder="e.g. Nyanza Peoples Union" onChange={(e) => setNewParty((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Abbreviation</label>
                <input className={input} value={newParty.abbr} placeholder="NPU" onChange={(e) => setNewParty((p) => ({ ...p, abbr: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Formation</label>
                <select className={input} value={newParty.blocId} onChange={(e) => setNewParty((p) => ({ ...p, blocId: e.target.value }))}>
                  <option value="">Unaligned</option>
                  {blocs.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            {addError && <p className="mt-2 text-xs font-medium text-red-500">{addError}</p>}
            <div className="mt-3 flex gap-2">
              <button type="submit" disabled={adding || newParty.name.trim().length < 2} className="rounded-sm bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
                {adding ? "Adding…" : "Add party"}
              </button>
              <button type="button" onClick={() => { setShowAdd(false); setAddError(null); }} className="rounded-sm border border-gray-200 px-4 py-2 text-xs font-semibold dark:border-white/10 dark:text-white">
                Cancel
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">Added parties are available to everyone creating a Kenya 2027 market.</p>
          </form>
        )}
      </div>

      {offHub.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The hub has tabs for President, Governor, Senate, National Assembly and Balance of power.{" "}
          {offHub.map((r) => r.name || r.id).join(", ")}{offHub.length === 1 ? " does not have one yet" : " do not have one yet"} — the market is still created and still reachable, it just has no dedicated tab.
        </p>
      )}
    </div>
  );
}
